import React, { useCallback, useEffect, useState } from 'react'
import { ICallFlowStep } from '@dataTypes/clinic.type'
import { AiVoiceTone, IAiReceptionistSettings } from '@dataTypes/ai.type'
import {
  fetchAiReceptionistSettingsApi,
  previewAiReceptionistVoiceApi,
  saveAiReceptionistSettingsApi,
} from '@services/api/ai-receptionist-settings.api'
import { useAppSelector } from '@store/hooks'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import {
  Button,
  InputField,
  MultiselectCombobox,
  Select,
  ToggleSwitch,
} from '@enamelai/design-system'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import {
  LANGUAGES,
  TONES,
  VOICES,
  emptyAiReceptionistSettings,
} from '@constants/index'
import { AiReceptionistFieldErrors } from '@dataTypes/index'
import { getAiReceptionistFieldErrors } from '@utils/ai.util'
import StepPanelShell from './StepPanelContainer.component'

type AiStep = Extract<ICallFlowStep, { type: 'ai_receptionist' }>

export type AiReceptionistPanelProps = {
  step: AiStep
  branchLabel: string
  channel?: 'call' | 'sms'
  onChange: (patch: Partial<AiStep>) => void
  onCancel: () => void
  onDone: () => void
}

const CALL_CHANNEL_HELPER =
  'This applies to phone calls. Text messages keep their own channel.'

const SMS_CHANNEL_HELPER =
  'This applies to text messages. Phone calls keep their own channel.'

const SMS_LANGUAGE_TOOLTIP = 'This will also be applied to SMS.'

const cloneAiReceptionistSettings = (
  value: IAiReceptionistSettings,
): IAiReceptionistSettings => ({
  ...value,
  controls: [...(value.controls ?? [])],
  languages: [...(value.languages ?? [])],
  verification_enabled: value.verification_enabled !== false,
  verification_required_for: [...(value.verification_required_for ?? [])],
  verification_fields: [...(value.verification_fields ?? [])],
  escalation_scenarios: [...(value.escalation_scenarios ?? [])],
  services: [...(value.services ?? [])],
  channels: [...(value.channels ?? [])],
})

const getVisibleFieldErrors = (
  settings: IAiReceptionistSettings,
): AiReceptionistFieldErrors => {
  const errors = getAiReceptionistFieldErrors(settings)
  const visible: AiReceptionistFieldErrors = {}
  if (errors.languages) visible.languages = errors.languages
  if (errors.voice_style) visible.voice_style = errors.voice_style
  if (errors.voice_tone) visible.voice_tone = errors.voice_tone
  return visible
}

const AiReceptionistPanel = ({
  branchLabel,
  channel = 'call',
  onCancel,
  onDone,
}: AiReceptionistPanelProps): React.ReactElement => {
  const clinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id ?? '',
  )
  const { showToast } = useNotificationToast()
  const [settings, setSettings] = useState<IAiReceptionistSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showFieldErrors, setShowFieldErrors] = useState(false)

  useEffect(() => {
    if (!clinicId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchAiReceptionistSettingsApi(clinicId)
      .then((response) => {
        if (cancelled) return
        const loaded =
          response.data.ai_receptionist_settings ??
          emptyAiReceptionistSettings()
        setSettings(
          cloneAiReceptionistSettings({ ...loaded, clinic_id: clinicId }),
        )
      })
      .catch(() => {
        if (cancelled) return
        setSettings(null)
        setError('Could not load AI receptionist settings.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [clinicId])

  const fieldErrors =
    showFieldErrors && settings ? getVisibleFieldErrors(settings) : {}
  const channelOn = Boolean(
    settings?.enabled && (settings.channels ?? []).includes(channel),
  )

  const playPreview = useCallback(
    async (feature?: string): Promise<void> => {
      if (!settings) return
      setPreviewing(true)
      try {
        let text: string | undefined
        if (feature === 'voice_style') {
          text = `Hello, this is ${
            settings.name.trim().length
              ? settings.name
              : `${settings.voice_style} voice.`
          }.`
        }
        const blob = await previewAiReceptionistVoiceApi(
          settings.voice_style,
          settings.voice_tone,
          text,
        )
        const url = URL.createObjectURL(blob)
        const audio = new Audio(url)
        audio.onended = () => URL.revokeObjectURL(url)
        await audio.play()
      } catch {
        showToast(
          createFailedToast(
            'Voice preview',
            'The selected voice could not be played.',
          ),
        )
      } finally {
        setPreviewing(false)
      }
    },
    [settings, showToast],
  )

  const handleSave = useCallback(async (): Promise<void> => {
    if (!settings || !clinicId) return
    const errors = getVisibleFieldErrors(settings)
    if (Object.keys(errors).length > 0) {
      setShowFieldErrors(true)
      return
    }
    setShowFieldErrors(false)
    const controls = Array.isArray(settings.controls) ? settings.controls : []
    const payload = {
      ...settings,
      clinic_id: clinicId,
      channels: Array.isArray(settings.channels) ? settings.channels : [],
      schedule_approved_types: controls.includes('new_booking'),
      reschedule_cancel:
        controls.includes('reschedule') || controls.includes('cancel'),
      answer_faqs: controls.includes('faq'),
      create_callback_tasks: controls.includes('callback'),
    }
    setSaving(true)
    try {
      await saveAiReceptionistSettingsApi(clinicId, payload)
      showToast(createSuccessToast('AI receptionist', 'Successfully saved.'))
      onDone()
    } catch {
      showToast(
        createFailedToast('AI receptionist', 'Could not save this setting.'),
      )
    } finally {
      setSaving(false)
    }
  }, [clinicId, onDone, settings, showToast])

  return (
    <StepPanelShell
      title="AI receptionist"
      editable
      confirmDisabled={saving || loading || settings == null}
      cancelDisabled={saving}
      branchLabel={branchLabel}
      onCancel={onCancel}
      onDone={() => {
        void handleSave()
      }}
    >
      {loading ? (
        <p className="scp-step-panel-muted">Loading configuration…</p>
      ) : error ? (
        <p className="scp-step-panel-muted">{error}</p>
      ) : settings ? (
        <>
          <div>
            <label className="scp-ai-receptionist-enable-row">
              <span className="ds-type-paragraph-small ds-type-weight-bold">
                Enable AI receptionist
              </span>
              <ToggleSwitch
                checked={channelOn}
                aria-label="Enable AI receptionist"
                onChange={(checked) => {
                  const nextChannels = new Set(settings.channels ?? [])
                  if (checked) nextChannels.add(channel)
                  else nextChannels.delete(channel)
                  const channels = Array.from(nextChannels)
                  setSettings({
                    ...settings,
                    channels,
                    enabled: checked ? true : channels.length > 0,
                  })
                }}
              />
            </label>
            <p className="scp-ai-receptionist-helper ds-type-paragraph-xsmall">
              {channel === 'sms' ? SMS_CHANNEL_HELPER : CALL_CHANNEL_HELPER}
            </p>
          </div>
          {settings.enabled ? (
            <div className="scp-ai-receptionist-panel-fields">
              <InputField
                fullWidth
                variant="text"
                label="Name"
                optional
                value={settings.name}
                onChange={(event) =>
                  setSettings({ ...settings, name: event.target.value })
                }
                onClear={() => setSettings({ ...settings, name: '' })}
              />
              <div className="scp-ai-receptionist-voice-field">
                <Select
                  label="Voice style"
                  placeholder="Select voice style"
                  value={settings.voice_style}
                  options={VOICES.map((voice) => ({
                    value: voice,
                    label: voice,
                  }))}
                  onChange={(value) =>
                    setSettings({
                      ...settings,
                      voice_style: value as string,
                    })
                  }
                  error={Boolean(fieldErrors.voice_style)}
                  helperText={fieldErrors.voice_style}
                />
                <Button
                  variant="text"
                  size="xs"
                  disabled={previewing}
                  onClick={() => {
                    void playPreview('voice_style')
                  }}
                >
                  Preview
                </Button>
              </div>
              <div className="scp-ai-receptionist-voice-field">
                <Select
                  label="Voice tone"
                  placeholder="Select voice tone"
                  value={settings.voice_tone}
                  options={TONES.map((tone) => ({
                    value: tone.value,
                    label: tone.label,
                  }))}
                  onChange={(value) =>
                    setSettings({
                      ...settings,
                      voice_tone: value as AiVoiceTone,
                    })
                  }
                  error={Boolean(fieldErrors.voice_tone)}
                  helperText={fieldErrors.voice_tone}
                />
                <Button
                  variant="text"
                  size="xs"
                  disabled={previewing}
                  onClick={() => {
                    void playPreview('voice_tone')
                  }}
                >
                  Preview
                </Button>
              </div>
              <div className="scp-ai-receptionist-languages">
                <div className="scp-ai-receptionist-languages-label">
                  <span>Supported languages</span>
                  <span
                    className="ds-section-card__icon-button"
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    title={SMS_LANGUAGE_TOOLTIP}
                  >
                    <InfoIcon aria-hidden="true" />
                  </span>
                </div>
                <MultiselectCombobox
                  className="scp-ai-receptionist-languages-field"
                  label="Supported languages"
                  placeholder="Search language"
                  value={settings.languages}
                  options={LANGUAGES.map((language) => ({
                    value: language,
                    label: language,
                  }))}
                  onChange={(value) =>
                    setSettings({
                      ...settings,
                      languages: value as string[],
                    })
                  }
                  error={Boolean(fieldErrors.languages)}
                  helperText={fieldErrors.languages}
                />
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </StepPanelShell>
  )
}

export default AiReceptionistPanel
